/**
 *
 *  order default value: true
 * 	true stands for ascending order
 *  false stands for descending order
 */
// eslint-disable-next-line import/prefer-default-export
export const sortBy = ({ field, type, order }, array) => {
  const sortingArray = [...array];
  if (sortingArray.length) {
    sortingArray.sort((data1, data2) => {
      const item1 = order ? data1[field] : data2[field];
      const item2 = order ? data2[field] : data1[field];

      switch (type) {
        case 'string':
          if (item1.toUpperCase() < item2.toUpperCase()) {
            return -1;
          }
          if (item2.toUpperCase() > item1.toUpperCase()) {
            return 1;
          }
          return 0;
        case 'date':
          return new Date(item1) - new Date(item2);
        default:
          return item1 - item2;
      }
    });
  }
  return sortingArray;
};

/**
 *
 * local storage
 *
 */

export const loadState = (name = '') => {
  if (typeof window === 'undefined') return undefined;
  try {
    const serializedState = sessionStorage.getItem(name);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    // Sentry.captureException(err);
    return undefined;
  }
};

export const saveState = (name, data) => {
  if (typeof window === 'undefined') return;
  try {
    const serializedState = JSON.stringify(data);
    sessionStorage.setItem(name, serializedState);
  } catch (err) {
    // Sentry.captureException(err);
  }
};

export const removeState = (name = '') => {
  if (typeof window === 'undefined') return;
  try {
    if (!name) {
      sessionStorage.clear();
    } else {
      sessionStorage.removeItem(name);
    }
  } catch (err) {
    // Sentry.captureException(err);
  }
};

export const doubleDigit = number => {
  if (number < 10) {
    return `0${number}`;
  }
  return number;
};

export const formatDateWithoutTime = date => {
  if (date) {
    const formattedDate = new Date(date);
    const day = doubleDigit(formattedDate.getUTCDate());
    const year = formattedDate.getUTCFullYear();
    const month = doubleDigit(formattedDate.getUTCMonth() + 1);
    const newDate = `${year}-${month}-${day}`;
    return new Date(`${newDate}T00:00:00.000Z`);
  }
  return '';
};

// Function to convert reward value as proper decimals(5 -> 5.00, 5.5 -> 5.50)
export const formatRewardValue = value => {
  if (!value && value !== 0) {
    return value;
  }
  const amountValue = Number(value);
  if (Number.isNaN(amountValue)) {
    return value;
  }
  return amountValue.toFixed(2);
};

// Utility function to generate short name
export const getShortName = name => {
  if (!name) return ''; // Handle empty names
  return name
    .split(' ') // Split the name into words
    .map(word => word.charAt(0).toUpperCase()) // Take the first letter of each word
    .join('') // Join the letters
    .slice(0, 2); // Restrict to a maximum length of 2
};

export function formatDateWithSuffix(rawDate) {
  if (!rawDate) return '';
  const date = new Date(rawDate); // ensure it's a Date object

  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();

  // Get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = n => {
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const suffix = getOrdinalSuffix(day);

  return `${day}${suffix} ${month} ${year}`;
}

export function formatTime(rawDate) {
  if (!rawDate) return '';
  const date = new Date(rawDate); // ensure it's a Date object
  const hours = date.getHours();
  const minutes = date.getMinutes();

  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHour = hours % 12 === 0 ? 12 : hours % 12;
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${formattedHour}:${formattedMinutes}${ampm}`;
}

// Compact labels matching the wallet passes: "Every day", "Weekdays",
// "Weekends", a range ("Mon to Thu") or a short list ("Mon, Wed, Fri").
// Days are 0=Sun … 6=Sat.
export function formatDaysRangeToString(daysRange) {
  if (!Array.isArray(daysRange)) {
    return '';
  }
  const set = new Set(daysRange.filter(d => d >= 0 && d <= 6));
  if (set.size === 0) return '';
  if (set.size >= 7) return 'Every day';

  const short = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const has = (...d) => d.every(x => set.has(x));
  if (set.size === 5 && has(1, 2, 3, 4, 5)) return 'Weekdays';
  if (set.size === 2 && has(0, 6)) return 'Weekends';

  const sorted = [...set].sort((a, b) => a - b);
  const consecutive = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  if (consecutive && sorted.length >= 3) {
    return `${short[sorted[0]]} to ${short[sorted[sorted.length - 1]]}`;
  }
  return sorted.map(d => short[d]).join(', ');
}

/**
 * Converts any valid date input to YYYY-MM-DD format.
 * Returns null if the input is invalid.
 *
 * @param {string | Date | number} dateInput
 * @returns {string | null} formatted date
 */
export function formatDateToYYYYMMDD(dateInput) {
  if (!dateInput) return null;

  const date = new Date(dateInput);
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(date.getTime())) return null; // invalid date

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 0-based months
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}
